import type { BiodynamicPreparation } from '@/types/biodynamic';
import { ZODIAC_TO_DAY_TYPE } from '@/types/biodynamic';
import type { BiodynamicDayType, MoonPhase } from '@/types/biodynamic';
import {
  getMoonZodiacSign,
  getMoonPhaseData,
  isMoonAscending as astroIsMoonAscending,
} from '@/lib/astronomy';

export const PREPARATIONS: BiodynamicPreparation[] = [
  {
    id: '500',
    name: 'Preparation 500',
    commonName: 'Horn Manure',
    ingredients: ['Fresh cow manure', 'Cow horn'],
    description: 'The foundational biodynamic soil preparation. Fresh cow manure is packed into a cow horn and buried underground over winter, allowing it to undergo a remarkable transformation through the life forces of the soil.',
    howToMake: `In autumn, fill fresh cow horns with high-quality cow manure from a biodynamic or demeter-certified herd. Bury the horns tip-down in a well-drained, living soil area, at least 60cm deep. Leave through winter until spring (approximately 6 months). The manure undergoes a humification process, becoming a concentrated, sweet-smelling substance utterly unlike its original form.\n\nUnearth in spring and store in an earthenware pot surrounded by peat moss in a cool, shaded place.`,
    howToUse: `Stir (dynamise) a very small quantity — about 25–50g per hectare — in 10–15 litres of warm rainwater for exactly one hour. Alternate between creating a deep vortex and chaotic turbulence. This stirring process is central to biodynamic practice.\n\nSpray onto soil in the late afternoon or evening as fine droplets, directing the spray downward toward the soil. Apply on a root day if possible, in autumn or spring.`,
    applicationAreas: ['vegetable-garden', 'farm', 'vineyard', 'orchard', 'compost'],
    timing: 'Apply in spring (March–April) and/or autumn (September–October). Best on root days. Apply in late afternoon.',
    benefits: [
      'Stimulates soil microbial life and humus formation',
      'Encourages deep root development',
      'Improves soil structure and water retention',
      'Enlivens the soil with cosmic forces (according to biodynamic theory)',
    ],
  },
  {
    id: '501',
    name: 'Preparation 501',
    commonName: 'Horn Silica',
    ingredients: ['Ground quartz crystal (silica)', 'Cow horn'],
    description: 'Where Preparation 500 works with the earthly, subterranean forces, Preparation 501 works with light and cosmic forces. Finely ground crystal quartz is packed into cow horns and buried over summer to capture the warmth and light forces of the season.',
    howToMake: `Grind quartz crystal to a fine flour-like powder. Mix with enough rainwater to form a stiff paste and pack tightly into cow horns. Bury in spring and leave through summer, unearthing in autumn. Store in a glass jar on a sunny windowsill.`,
    howToUse: `Stir a tiny quantity — 1g per hectare, sometimes less — in 10 litres of warm rainwater for one hour, alternating vortex and chaos. Spray as a fine mist over the aerial parts of plants in the early morning, allowing it to drift over the crop. Apply on a fruit or flower day.`,
    applicationAreas: ['vegetable-garden', 'farm', 'vineyard', 'orchard'],
    timing: 'Apply in the morning on fruit or flower days. Use on growing crops to enhance flavour, quality, and disease resistance. Do NOT use in periods of drought.',
    benefits: [
      'Improves light utilisation and photosynthesis',
      'Enhances flavour and aroma of fruits and vegetables',
      'Strengthens plant immunity to fungal diseases',
      'Improves keeping quality of harvested produce',
    ],
  },
  {
    id: '502',
    name: 'Preparation 502',
    commonName: 'Yarrow Compost Preparation',
    ingredients: ['Yarrow flowers (Achillea millefolium)', 'Stag\'s bladder'],
    description: 'One of six compost preparations based on medicinal herbs. Yarrow flowers are packed into a stag\'s bladder and hung in the sun over summer, then buried through winter. It mediates the relationship between potassium and sulphur in the compost.',
    howToMake: 'Collect yarrow flowers at their peak. Pack into a stag\'s bladder and hang in the full summer sun. Bury in autumn and retrieve in spring.',
    howToUse: 'Insert a small quantity into the compost heap at 5 equidistant points on the heap, together with preparations 503–507. Cover insertion points with soil.',
    applicationAreas: ['compost', 'farm', 'vegetable-garden'],
    timing: 'Insert into compost heap when building or turning it.',
    benefits: [
      'Regulates potassium and sulphur dynamics',
      'Promotes healthy fermentation in compost',
      'Said to give the compost the ability to attract trace elements',
    ],
  },
  {
    id: '503',
    name: 'Preparation 503',
    commonName: 'Chamomile Compost Preparation',
    ingredients: ['Chamomile flowers (Matricaria chamomilla)', 'Cow intestine'],
    description: 'Chamomile flowers buried in a cow\'s small intestine over winter. Works to stabilise nitrogen in the compost and stimulate plant growth.',
    howToMake: 'Collect chamomile flowers and pack into a length of cow intestine. Bury in soil over winter in a sunny location.',
    howToUse: 'Insert into the compost heap alongside the other compost preparations (502–507).',
    applicationAreas: ['compost', 'farm', 'vegetable-garden'],
    timing: 'Add when building or turning a compost heap.',
    benefits: [
      'Stabilises nitrogen compounds in compost',
      'Promotes healthy plant growth',
      'Brings calcium-potassium balance',
    ],
  },
  {
    id: '505',
    name: 'Preparation 505',
    commonName: 'Oak Bark Compost Preparation',
    ingredients: ['Oak bark (Quercus robur)', 'Skull of domesticated animal'],
    description: 'Ground oak bark, rich in calcium, is prepared in the skull of a domestic animal and buried over winter. Brings plant-supporting calcium forces to the compost.',
    howToMake: 'Grind fresh oak bark and pack into an animal skull. Place in a water-logged area or barrel of water over winter.',
    howToUse: 'Insert into compost heap with the other preparations.',
    applicationAreas: ['compost', 'farm', 'vineyard'],
    timing: 'Add when building or turning a compost heap.',
    benefits: [
      'Counters plant disease tendencies',
      'Brings healthy calcium forces',
      'Strengthens plant resistance',
    ],
  },
  {
    id: '507',
    name: 'Preparation 507',
    commonName: 'Valerian Compost Preparation',
    ingredients: ['Valerian flowers (Valeriana officinalis)'],
    description: 'Unlike the other compost preparations, valerian does not use an animal sheath. The juice of valerian flowers is simply fermented and diluted. It stimulates phosphorus activity in the compost and protects against frost.',
    howToMake: 'Press the juice of valerian flowers and allow to ferment slightly. Dilute a small amount in water before use.',
    howToUse: 'Spray over the outside of the compost heap after inserting the other preparations. The warmth of valerian creates a protective "coat" around the heap. Also spray on crops at risk of frost.',
    applicationAreas: ['compost', 'vegetable-garden', 'vineyard', 'orchard'],
    timing: 'Spray on compost and on crops threatened by late frosts.',
    benefits: [
      'Stimulates phosphorus activity',
      'Creates warmth forces in compost',
      'Offers some frost protection when sprayed on plants',
      'Completes and enlivens the whole compost preparation process',
    ],
  },
  {
    id: '508',
    name: 'Preparation 508',
    commonName: 'Equisetum (Horsetail) Tea',
    ingredients: ['Equisetum arvense (Common Horsetail)'],
    description: 'Not strictly one of Steiner\'s original preparations, but widely used in biodynamic practice. A decoction of horsetail (a silica-rich ancient plant) sprayed on crops to prevent fungal diseases, particularly in humid conditions.',
    howToMake: `Simmer 10g of dried horsetail (or 30–60g of fresh) in 1 litre of water for 20 minutes. Allow to cool. Dilute to 5 litres and ferment for 3–5 days. Strain well before use.\n\nAlternatively: steep fresh or dried horsetail in cold water for 24 hours without boiling.`,
    howToUse: `Spray undiluted (or diluted 1:5) over the aerial parts of plants in the morning. Apply before a period of humidity or rain, or when fungal disease is present. Particularly valuable in vineyards.`,
    applicationAreas: ['vegetable-garden', 'farm', 'vineyard', 'orchard'],
    timing: 'Apply as a preventive spray in humid conditions. Best on leaf days.',
    benefits: [
      'Rich in silica — strengthens plant cell walls against fungal penetration',
      'Effective preventive treatment for downy mildew, blight, and botrytis',
      'Particularly valued in vineyard management for mildew control',
      'A natural, permitted treatment in certified organic and biodynamic farming',
    ],
  },
];

// Calendar helpers — backed by accurate astronomy-engine ephemeris
export function getBiodynamicDayType(date: Date): BiodynamicDayType {
  return ZODIAC_TO_DAY_TYPE[getMoonZodiacSign(date)];
}

export function getMoonPhase(date: Date): MoonPhase {
  return getMoonPhaseData(date).phase;
}

export function isMoonAscending(date: Date): boolean {
  return astroIsMoonAscending(date);
}

export const BIODYNAMIC_INTRO = `Biodynamic farming and gardening was developed by Rudolf Steiner in 1924, following requests from farmers who were noticing declining soil fertility and plant health after the introduction of chemical fertilisers. Steiner delivered a series of lectures — the "Agriculture Course" — at Koberwitz (now in Poland), which formed the foundation of the movement.

Biodynamic practice views the farm or garden as a self-sustaining organism, with soil, plants, animals, and cosmic forces all interconnected. It goes beyond organic farming to embrace a spiritual-scientific understanding of agriculture rooted in Anthroposophy.

The key principles include:
- Using the eight biodynamic preparations (500–508) made from herbs, minerals, and animal materials
- Following the biodynamic planting calendar, which coordinates planting, cultivating, and harvesting with lunar and stellar rhythms
- Building soil fertility through composting, cover crops, and minimal tillage
- Maintaining biodiversity and ecological balance on the holding
- Striving for a closed-cycle farm organism, reducing inputs from outside

Maria Thun (1922–2012) was instrumental in developing the biodynamic planting calendar through decades of meticulous experiments on a farm in Germany, recording how sowing on different days correlated with root, leaf, flower, and fruit quality.`;
